// ── Field Ticket Reconciliation: the pure part ──
//
// A field ticket is built off the live HeavyJob cards, and the cards stay
// editable after the client signs. JDE payroll posts whatever the card says at
// payroll time and the invoice bills JDE — so a card edited after signing
// bills hours the client never signed for, and nothing said so. Every ticket
// send now writes the HeavyJob values it was built from into
// PC.Field_Ticket_Reconciliation (fieldTicketReconciliation.js), one row per
// work date x shift x person (or unit) x cost code — the "Labor & Equipment
// Billing Key" grain — next to the hours the ticket printed. The
// Reconciliation view reads them back two ways:
//
//   Field Ticket — the cached HeavyJob hours vs the cards as they are now
//                  (compareHeavyJob): did anybody touch a card after the
//                  ticket went out?
//   Invoice      — the ticket hours vs the JDE cost history lines the invoice
//                  bills (matchJde), and whether those lines are billed.
//
// Everything here is pure: the approval tab feeds reconRowsForDay the day it
// just ticketed, the view feeds the rest the stored rows, the live cards and
// the billing lines.

import { ticketForShift } from './billingDailyLabor';
import { isRentUsage } from './billingRentalUsage';
import { invoicePeriod, mergeInvoiceEnvelopes } from './billingWeekTracker';

export const RECON_TOL = 0.01;
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
export const r2 = (n) => Math.round(num(n) * 100) / 100;
const HOURS = new Set(['Regular', 'Overtime']);

// HeavyJob keys an employee by code, JDE by address number — the same number,
// but one side can carry leading zeros or padding. Units and cost codes are
// compared trimmed and upper-cased.
export const normId = (v) => {
    const s = String(v ?? '').trim();
    return /^\d+$/.test(s) ? String(Number(s)) : s.toUpperCase();
};
export const normCode = (v) => String(v ?? '').trim().toUpperCase();

// A row's identity inside one work date: kind, person-or-unit, cost code.
const idOf = (r) => (r.k === 'Equipment' ? normCode(r.unit) : normId(r.emp));
export const dayKey = (r) => `${r.d}|${r.k}|${idOf(r)}|${normCode(r.cc)}`;
// A review's key (PC.Field_Ticket_Recon_Review): the day key with the crew,
// so a person on both shifts of one day reviews as two rows. Ticketless
// rows (New in HeavyJob, JDE no ticket) carry no crew.
export const reviewKey = (r) => `${r.d}|${r.sh || ''}|${r.k}|${idOf(r)}|${normCode(r.cc)}`;

export const REVIEWED = 'Reviewed';
export const REVIEW_STALE = 'Changed since review';

/**
 * The reviews read back (fieldTicketReconReviews) as a map by key:
 * { key, d, note, by, at, hjStatus, hjDelta, jdeStatus, jdeDelta }.
 */
export const reviewMap = (list) => {
    const m = {};
    (list || []).forEach((x) => {
        if (!x?.Row_Key) return;
        m[x.Row_Key] = {
            key: x.Row_Key, d: String(x.Date_Worked || '').slice(0, 10), note: x.Note || '',
            by: x.Reviewed_By || '', at: x.Reviewed_At_Utc || null,
            hjStatus: x.HJ_Status || null, hjDelta: x.HJ_Delta == null ? null : num(x.HJ_Delta),
            jdeStatus: x.JDE_Status || null, jdeDelta: x.JDE_Delta == null ? null : num(x.JDE_Delta),
        };
    });
    return m;
};

/**
 * Hang each row's review on it as `rv` (the review plus `staleTicket` /
 * `staleInvoice`): a check that has moved since the review was written —
 * another status, or hours more than `tol` from what was reviewed —
 * re-flags the row on that tab. A side the review never saw (HeavyJob not
 * read at the time) can't go stale.
 */
export const applyReviews = (rows, reviews, { tol = RECON_TOL } = {}) => {
    if (!reviews || !Object.keys(reviews).length) return rows;
    const moved = (nowStatus, nowDelta, wasStatus, wasDelta) => wasStatus != null
        && (String(nowStatus || '') !== wasStatus || Math.abs(num(nowDelta) - num(wasDelta)) > tol);
    return rows.map((r) => {
        const v = reviews[reviewKey(r)];
        if (!v) return r;
        return {
            ...r,
            rv: {
                ...v,
                staleTicket: r.hjNow != null && moved(r.status, r.delta, v.hjStatus, v.hjDelta),
                staleInvoice: moved(r.jdeStatus, r.jdeDelta, v.jdeStatus, v.jdeDelta),
            },
        };
    });
};

/** What a review of this row records of each check, for the stale test later. */
export const reviewPayload = (r, note) => ({
    key: reviewKey(r), d: r.d, note,
    hjStatus: r.hjNow != null ? r.status || null : null,
    hjDelta: r.hjNow != null ? r2(r.delta) : null,
    jdeStatus: r.jdeStatus || null,
    jdeDelta: r.jdeDelta == null ? null : r2(r.jdeDelta),
});

// ── Capture ──

/**
 * HeavyJob-shaped rows (readHeavyJobCards / HEET, job rules applied) rolled
 * up per kind x person-or-unit x cost code: regular and overtime hours, per
 * diem adders (readHeavyJobCards puts a person's adder on their biggest cost
 * code, one per day), the cards they came off, the newest card edit, and the
 * card shift.
 */
export const hjRollup = (rows, cards = []) => {
    const modOf = {};
    (cards || []).forEach((c) => { if (c?.id) modOf[c.id] = c.lastModified || null; });
    const out = {};
    (rows || []).forEach((r) => {
        const equip = r.Record_Type === 'Equipment';
        const pd = r.Record_Type === 'Per Diem';
        // 'Hours': the nightly SQL copy's figure, straight time and overtime
        // together (readHeavyJobCards { source: 'sql' }).
        const unsplit = r.Record_Type === 'Hours';
        if (!equip && !pd && !unsplit && !HOURS.has(r.Record_Type)) return;
        const h = num(r.Hours);
        if ((!h && !pd) || !r.Cost_Code) return;
        const id = equip ? normCode(r.Equipment_Code || r.Equipment_Code_Description) : normId(r.Staff_ID);
        if (!id) return;
        const k = equip ? 'Equipment' : 'Labor';
        const key = `${k}|${id}|${normCode(r.Cost_Code)}`;
        const e = out[key] || (out[key] = {
            k, id, cc: String(r.Cost_Code).trim(), ccd: r.Cost_Code_Description || '',
            emp: equip ? '' : String(r.Staff_ID).trim(), name: equip ? '' : String(r.Staff_Name || '').trim(),
            title: equip ? '' : String(r.Pay_Class_Description || '').trim(),
            unit: equip ? String(r.Equipment_Code || r.Equipment_Code_Description || '').trim() : '',
            unitDesc: equip ? String(r.Equipment_Code_Description || '').trim() : '',
            st: 0, ot: 0, pd: 0, cards: new Set(), mod: null, night: false,
        });
        if (pd) e.pd += 1;
        else if (unsplit) { e.st += h; e.unsplit = true; }
        else if (equip || r.Record_Type === 'Regular') e.st += h;
        else e.ot += h;
        if (!e.title && r.Pay_Class_Description) e.title = String(r.Pay_Class_Description).trim();
        if (r.Card_ID) {
            e.cards.add(String(r.Card_ID));
            const m = modOf[r.Card_ID];
            if (m && (!e.mod || m > e.mod)) e.mod = m;
        }
        if (Number(r.Shift) === 2) e.night = true;
    });
    return out;
};

/**
 * The rows one ticketed date writes: every person and unit on the date's
 * ticket for the given shifts, plus anybody HeavyJob has that the ticket left
 * off (the reviewed time card struck them, or moved their hours), each with
 * HeavyJob's hours at this moment and the ticket's.
 *
 *   ticket   — the whole day's ticket (the approval tab's dayTicket): its
 *              labor shift split and classifications are the ones printed
 *   hjRows   — the day's HeavyJob rows with the job rules applied, NOT the
 *              reviewed card (so ticket vs HeavyJob shows the review)
 *   cards    — the day's card headers (for card edit times)
 *   shifts   — the shifts of this date the send covers
 *   basis    — 'cards' (live HeavyJob API) or 'heet'
 */
export const reconRowsForDay = ({ date, shifts, ticket, hjRows, cards, basis }) => {
    const want = new Set(shifts || []);
    const t = ticket || { labor: [], equipment: [], codes: [] };
    const src = basis === 'cards' ? 'hcss-api' : basis ? String(basis) : null;
    const ccDesc = {};
    (t.codes || []).forEach((c) => { ccDesc[normCode(c.code)] = c.desc || ''; });
    const hj = hjRollup(hjRows, cards);

    // Where each person printed, and the equipment's sheet (day, unless the
    // date is night-only) — billingDailyLabor.ticketForShift's rule.
    const personShift = {};
    (t.labor || []).forEach((p) => { if (p.id) personShift[normId(p.id)] = p.night ? 'Night' : 'Day'; });
    const hasDay = (t.labor || []).some((p) => !p.night);
    const nightOnly = !hasDay && (t.labor || []).some((p) => p.night);
    const equipShift = nightOnly ? 'Night' : 'Day';

    const rows = {};
    const at = (sh, k, id, cc, init) => {
        const key = `${sh}|${k}|${id}|${normCode(cc)}`;
        if (!rows[key]) {
            rows[key] = {
                d: date, sh, k, cc: String(cc).trim(), ccd: ccDesc[normCode(cc)] || '', src,
                emp: '', name: '', unit: '', unitDesc: '', title: '', cls: '',
                card: '', hjMod: null, hjSt: 0, hjOt: 0, hj: 0, hjPd: 0, ft: 0, ...init,
            };
        }
        return rows[key];
    };

    [...want].forEach((sh) => {
        const st = ticketForShift(t, sh);
        (st.labor || []).forEach((p) => {
            if (!p.id) return;
            Object.entries(p.hours || {}).forEach(([cc, h]) => {
                if (!(num(h) > 0)) return;
                const r = at(sh, 'Labor', normId(p.id), cc, { emp: String(p.id).trim() });
                r.ft = r2(r.ft + num(h));
                r.name = p.name || r.name;
                r.title = p.payClass || r.title;
                r.cls = p.cls || r.cls;
            });
        });
        (st.equipment || []).forEach((e) => {
            const unit = String(e.unit || e.desc || '').trim();
            if (!unit) return;
            Object.entries(e.hours || {}).forEach(([cc, h]) => {
                if (!(num(h) > 0)) return;
                const r = at(sh, 'Equipment', normCode(unit), cc, { unit });
                r.ft = r2(r.ft + num(h));
                r.unitDesc = e.desc || r.unitDesc;
            });
        });
    });

    Object.values(hj).forEach((e) => {
        // Off the ticket entirely: the card's own shift says which sheet the
        // person would have been on.
        const sh = e.k === 'Equipment' ? equipShift : personShift[e.id] || (e.night ? 'Night' : 'Day');
        if (!want.has(sh)) return;
        const r = at(sh, e.k, e.id, e.cc, e.k === 'Equipment' ? { unit: e.unit } : { emp: e.emp });
        r.hjSt = r2(r.hjSt + e.st);
        r.hjOt = r2(r.hjOt + e.ot);
        r.hj = r2(r.hjSt + r.hjOt);
        r.hjPd = r2(r.hjPd + e.pd);
        r.card = [...new Set([...String(r.card || '').split(',').filter(Boolean), ...e.cards])].join(',');
        if (e.mod && (!r.hjMod || e.mod > r.hjMod)) r.hjMod = e.mod;
        if (!r.ccd) r.ccd = e.ccd;
        if (e.k === 'Labor') {
            if (!r.name) r.name = e.name;
            if (!r.title) r.title = e.title;
        } else if (!r.unitDesc) {
            r.unitDesc = e.unitDesc;
        }
    });
    return Object.values(rows).sort(rowOrder);
};

// ── Reading stored rows back ──

// The table's columns (the Labor & Equipment Billing Key), as the view names them.
const sigsFromDb = (x, grp) => ['1st', '2nd', '3rd']
    .map((n) => ({ email: x[`${grp}_${n}_Signature_Email`] || '', at: x[`${grp}_${n}_Signature_Date_Utc`] || null }))
    .filter((g) => g.email);
export const fromDb = (x) => ({
    id: x.RowID,
    job: x.Job_Number || '',
    snap: x.Snapshot_ID,
    docId: x.Doc_ID || '',
    part: x.Part || '',
    env: x.Envelope_RowID || null,
    // The envelopes a capture (or the backfill) pinned the row to, and the
    // last Record kept: they win over the document / week lookups.
    dailyEnvId: x.Daily_Envelope_RowID || null,
    weeklyEnvId: x.Weekly_Envelope_RowID || null,
    d: String(x.Date_Worked || '').slice(0, 10),
    sh: x.Shift,
    k: x.Type,
    cc: x.Cost_Code || '',
    ccd: x.Cost_Code_Description || '',
    card: x.HJ_Time_Card_Key || '',
    hjMod: x.HJ_Last_Modified_Utc || null,
    src: x.HJ_Source || '',
    emp: x.Emp_ID || '',
    name: x.Emp_Name || '',
    unit: x.Equipment_ID || '',
    unitDesc: x.Equipment_Description || '',
    eqTier: x.Strike_Equipment_Tier || '',
    eqBill: x.Equipment_Billing_Tier || '',
    title: x.Strike_Employee_Title || '',
    cls: x.Employee_Billing_Classification || '',
    hjSt: num(x.HJ_St_Hours),
    hjOt: num(x.HJ_OT_Hours),
    hj: num(x.HJ_Hours),
    ft: num(x.Field_Ticket_Hours),
    jdeKey: x.JDE_Job_Cost_History_Key || '',
    jdeSt: x.JDE_St_Hours == null ? null : num(x.JDE_St_Hours),
    jdeOt: x.JDE_OT_Hours == null ? null : num(x.JDE_OT_Hours),
    jde: x.JDE_Total_Hours == null ? null : num(x.JDE_Total_Hours),
    hjPd: num(x.Heavy_Job_Per_Diem),
    jdePd: x.JDE_Per_Diem == null ? null : num(x.JDE_Per_Diem),
    invHrs: x.Invoice_Hours == null ? null : num(x.Invoice_Hours),
    invSt: x.Invoice_St_Hours == null ? null : num(x.Invoice_St_Hours),
    invOt: x.Invoice_OT_Hours == null ? null : num(x.Invoice_OT_Hours),
    // What the last Record (or the capture) stamped. The view recomputes
    // the statuses live from the envelopes and summaries.
    daily: x.Daily_Field_Ticket_Status || '',
    weekly: x.Weekly_Field_Ticket_Status || '',
    invoice: x.Invoice_Status || '',
    sigsSaved: { daily: sigsFromDb(x, 'Daily_FT'), weekly: sigsFromDb(x, 'Weekly_FT'), invoice: sigsFromDb(x, 'IN') },
    inv: x.Invoice_No || '',
    source: x.Capture_Source || '',
    by: x.Captured_By || '',
    at: x.Captured_At_Utc || null,
    recBy: x.Reconciled_By || '',
    recAt: x.Reconciled_At_Utc || null,
});

/**
 * One snapshot per (work date, shift): the newest by default, or the first
 * one ('first') — what the ticket said when it first went out. Older
 * snapshots of a cell are history; a later send replaces the whole cell, so a
 * person the later ticket dropped is gone with it.
 */
export const currentRows = (rows, mode = 'latest') => {
    const pick = {};
    (rows || []).forEach((r) => {
        const cell = `${r.d}|${r.sh}`;
        const p = pick[cell];
        const better = !p || (mode === 'first' ? r.id < p.id : r.id > p.id);
        if (better) pick[cell] = { id: r.id, snap: r.snap };
    });
    return (rows || []).filter((r) => pick[`${r.d}|${r.sh}`]?.snap === r.snap);
};

// ── The three statuses: daily ticket, weekly ticket, invoice ──
//
// The billing key's status list (New Fusion Billing Table.xlsx), the same six
// for each level:
//   Pending          Daily: stored, awaiting the ticket. Weekly: waiting on
//                    the week summary. Invoice: every daily and weekly ticket
//                    is signed, nothing billed yet.
//   Draft            the ticket / week summary / invoice summary exists (a
//                    DocuSign draft, or an envelope voided back to the desk)
//   Sent             out for approval, nobody has signed
//   Routing          the first signer has signed, the rest are routing
//   Draft - Rejected an approver declined it (or AR kicked the summary back)
//   Signed           every approver has signed
export const STATUS = {
    pending: 'Pending', draft: 'Draft', sent: 'Sent', routing: 'Routing', rejected: 'Draft - Rejected', signed: 'Signed',
};
const lower = (v) => String(v || '').toLowerCase();
const DEAD = ['voided', 'expired', 'failed'];

/** An envelope's signers in routing order: [{ email, at, signed, declined }]. */
export const signersOf = (env) => {
    if (!env) return [];
    const list = (env.recipients || []).slice().sort((a, b) => (num(a.Routing_Order) - num(b.Routing_Order))
        || String(a.Recipient_ID || '').localeCompare(String(b.Recipient_ID || ''), undefined, { numeric: true }));
    if (list.length) {
        return list.map((r) => ({
            email: String(r.Email || '').trim(),
            name: String(r.Name || r.Email || '').trim(),
            signed: Boolean(r.Signed_At_Utc) || lower(r.Status) === 'completed',
            declined: Boolean(r.Declined_At_Utc) || lower(r.Status) === 'declined',
            at: r.Signed_At_Utc || null,
        }));
    }
    // Envelopes from before the recipient table carry their one signer.
    if (!env.Signer_Email) return [];
    const done = lower(env.Status) === 'completed';
    return [{ email: String(env.Signer_Email).trim(), name: String(env.Signer_Name || env.Signer_Email).trim(), signed: done, declined: lower(env.Status) === 'declined', at: done ? env.Completed_At_Utc || null : null }];
};

/** Where one envelope stands, on the key's status list (null: no envelope). */
export const envStatus = (env) => {
    if (!env) return null;
    const s = lower(env.Status);
    const sigs = signersOf(env);
    if (s === 'completed') return STATUS.signed;
    if (s === 'declined' || sigs.some((x) => x.declined)) return STATUS.rejected;
    if (s === 'sent' || s === 'delivered') return sigs.some((x) => x.signed) ? STATUS.routing : STATUS.sent;
    // created = a DocuSign draft; voided / expired / failed hand the
    // document back to be fixed and sent again.
    return STATUS.draft;
};

// A billing summary's stage, on the same list: the invoice's status until
// an approval envelope says more.
const SUMMARY_STATUS = { Draft: STATUS.draft, 'Kicked Back': STATUS.rejected, 'Uploaded to FTMS': STATUS.sent, Invoiced: STATUS.signed };
const RANK = [STATUS.rejected, STATUS.pending, STATUS.draft, STATUS.sent, STATUS.routing, STATUS.signed];

/**
 * Everything the statuses are read from, indexed once per render:
 *   envelopes  readBillingSignatures (recipients nested)
 *   summaries  [{ no, status, first, last }] the billing summaries and the
 *              work dates their lines span
 * Ticket envelopes are found by document (Shift 'Doc', Doc_ID), by week
 * (Shift 'Week-Day' / 'Week-Night', the packet scripts' summaries) or by
 * date and crew (per-day envelopes from before documents). Invoice approvals
 * are 'Doc' envelopes whose Doc_ID starts with 'inv' (invf00294169), matched
 * to their summary by summaryEnvelope.
 *   docs       parseDocs(userRows), the Ticket Map documents: a week summary
 *              on its own (kind 'summary', over days on other tickets) is
 *              each of its days' weekly ticket
 */
export const statusContext = ({ envelopes, summaries, weekEnding, docs } = {}) => {
    const doc = {};
    const week = {};
    const day = {};
    const inv = [];
    const newer = (m, k, e) => { if (k && (!m[k] || num(e.RowID) > num(m[k].RowID))) m[k] = e; };
    const byRow = {};
    (envelopes || []).forEach((e) => { if (e?.RowID) byRow[e.RowID] = e; });
    // An invoice approved in several envelopes is one approval (mergeInvoiceEnvelopes).
    mergeInvoiceEnvelopes(envelopes).forEach((e) => {
        const d = String(e.Work_Date || '').slice(0, 10);
        const wk = /^Week-(Day|Night)$/.exec(String(e.Shift || ''));
        if (e.Shift === 'Doc') {
            if (/^inv/i.test(String(e.Doc_ID || ''))) inv.push(e);
            else newer(doc, e.Doc_ID, e);
        } else if (wk && weekEnding) newer(week, `${weekEnding(d)}|${wk[1]}`, e);
        else if (e.Shift === 'Day' || e.Shift === 'Night') newer(day, `${d}|${e.Shift}`, e);
    });
    const sums = {};
    (summaries || []).forEach((v) => { if (v?.no) sums[v.no] = v; });
    // Day|shift -> the week summary on its own over it ({ env } — null while
    // it is a Draft with no envelope): the newest envelope, else the newest.
    const sumCell = {};
    const putSum = (cells, env) => cells.forEach((k) => {
        const had = sumCell[k];
        if (!had || (env && (!had.env || num(env.RowID) > num(had.env.RowID)))) sumCell[k] = { env };
    });
    // The app sends a summary document as `<id>s`; the field-billing scripts
    // send theirs under the document's own id (j71s0921to0927), which read as
    // a Draft though Brad and Ryan had signed it (Seth 11/4).
    const mapped = new Set();
    (docs || []).filter((x) => x?.kind === 'summary' && !x.del).forEach((x) => {
        mapped.add(x.id);
        putSum(x.cells || [], doc[`${x.id}s`] || doc[x.id] || null);
    });
    // A script week summary with no Ticket Map document (71's j71s1012to1018):
    // its id names its days, its work date the year they end in.
    Object.keys(doc).forEach((id) => {
        const m = /^j\d+s(\d\d)(\d\d)to(\d\d)(\d\d)$/.exec(id);
        if (!m || mapped.has(id)) return;
        const endY = Number(String(doc[id].Work_Date || '').slice(0, 4));
        if (!endY) return;
        const from = Date.UTC(m[1] > m[3] ? endY - 1 : endY, m[1] - 1, Number(m[2]));
        const to = Date.UTC(endY, m[3] - 1, Number(m[4]));
        const cells = [];
        for (let t = from; t <= to && cells.length < 62; t += 86400000) {
            const d = new Date(t).toISOString().slice(0, 10);
            cells.push(`${d}|Day`, `${d}|Night`);
        }
        putSum(cells, doc[id]);
    });
    return { doc, week, day, inv, sums, weekEnding, byRow, sumCell };
};

// The document id a snapshot's part hangs off: partDocId adds d / n / s.
const baseDoc = (r) => (['day', 'night', 'summary'].includes(r.part) ? String(r.docId || '').slice(0, -1) : String(r.docId || ''));

/**
 * The invoice approval envelope closing out one billing summary
 * ({ no, first, last } — first/last the work dates its lines span), from
 * `inv` (statusContext's 'inv…' envelopes); null while none has gone out.
 * An issued summary's number is the JDE invoice number the approval rides
 * on (Doc_ID 'inv<no>'), so that wins. Otherwise the newest approval whose
 * billing period (invoicePeriod — the "9.7.26-9.20.26" in its name, else
 * its work date's week) overlaps the span. Not "dated within a week after":
 * the next period's approval lands there too (invf00294240, dated 9/27,
 * read as F00294169's over 9/7–9/20). An own approval that is dead gives way
 * to a live one over the span: F00294169's was voided when FTMS reissued the
 * invoice as F00294315 (10/20/26), which Anna had approved under its new
 * number.
 */
export const summaryEnvelope = (v, inv) => {
    const newest = (list) => list.reduce((a, e) => (!a || num(e.RowID) > num(a.RowID) ? e : a), null);
    const live = (e) => !DEAD.includes(lower(e.Status));
    const mine = v?.no ? (inv || []).filter((e) => lower(e.Doc_ID) === `inv${lower(v.no)}`) : [];
    const own = newest(mine.filter(live));
    if (own || !v?.first || !v?.last) return own || newest(mine);
    const over = (inv || []).filter((e) => {
        const p = invoicePeriod(e);
        return p.from && p.to && p.from <= v.last && p.to >= v.first;
    });
    return newest(over.filter(live)) || newest(mine) || newest(over);
};

/**
 * The invoice approval envelope standing over one work date on a job whose
 * invoices were priced outside the Billing tab (54026071: the approvals
 * went out by script, no billing summary names their lines). Only an
 * approval whose name states its billing period counts: a mobilization or
 * third-party approval has none, and its work date's week would claim
 * labor it never billed. The newest live one, else the newest.
 */
export const periodEnvelope = (d, inv) => {
    const newest = (list) => list.reduce((a, e) => (!a || num(e.RowID) > num(a.RowID) ? e : a), null);
    if (!d) return null;
    const over = (inv || []).filter((e) => {
        const p = invoicePeriod(e);
        return p.named && p.from <= d && p.to >= d;
    });
    return newest(over.filter((e) => !DEAD.includes(lower(e.Status)))) || newest(over);
};

/**
 * A row's three statuses, their signers (first three, { email, at }), who
 * each one is still waiting on (`waiting`, names) and the envelopes they
 * came from. Rows with no ticket (JDE lines nobody ticketed)
 * carry only the invoice side.
 */
export const statusesOf = (r, ctx) => {
    const c = ctx || statusContext();
    const out = {
        daily: '', weekly: '', invoice: '',
        dailyEnv: null, weeklyEnv: null, invoiceEnv: null,
        sigs: { daily: [], weekly: [], invoice: [] },
        waiting: { daily: [], weekly: [], invoice: [] },
    };
    // Who an envelope still out is waiting on; nobody once it is done or dead.
    const waitOn = (env) => (env && ['sent', 'delivered'].includes(lower(env.Status))
        ? signersOf(env).filter((x) => !x.signed && !x.declined).map((x) => x.name || x.email) : []);
    const pick = (env) => signersOf(env).slice(0, 3).map((x) => ({ email: x.email, at: x.signed ? x.at : null }));
    if (r.id) {
        const base = baseDoc(r);
        // A pinned envelope first (script-sent tickets: Week-Day rows, the
        // j70s… summaries), then the ticket document's parts, then the week
        // and per-day envelopes.
        // A pin voided and sent again gives way to the newer envelope.
        const pinOr = (pin, looked) => (pin && !(DEAD.includes(lower(pin.Status)) && looked && num(looked.RowID) > num(pin.RowID)) ? pin : looked);
        const dEnv = pinOr(r.dailyEnvId && c.byRow?.[r.dailyEnvId],
            (base && (c.doc[`${base}${r.sh === 'Night' ? 'n' : 'd'}`] || c.doc[base])) || c.day[`${r.d}|${r.sh}`] || null) || null;
        // The week summary: the ticket's own summary send, else a week
        // summary on its own over the day, else the week envelope.
        const sumOver = c.sumCell?.[`${r.d}|${r.sh}`] || null;
        const wEnv = pinOr(r.weeklyEnvId && c.byRow?.[r.weeklyEnvId],
            (base && c.doc[`${base}s`]) || sumOver?.env || (c.weekEnding ? c.week[`${c.weekEnding(r.d)}|${r.sh}`] : null) || null) || null;
        out.daily = envStatus(dEnv) || r.daily || (r.docId ? STATUS.draft : STATUS.pending);
        out.weekly = envStatus(wEnv) || (sumOver ? STATUS.draft : '') || r.weekly || STATUS.pending;
        out.dailyEnv = dEnv?.RowID || r.dailyEnvId || null;
        out.weeklyEnv = wEnv?.RowID || r.weeklyEnvId || null;
        out.sigs.daily = pick(dEnv);
        out.sigs.weekly = pick(wEnv);
        out.waiting.daily = waitOn(dEnv);
        out.waiting.weekly = waitOn(wEnv);
    }
    // The invoice: the approval envelope closing out the row's summary, else
    // the summary's own stage, else Pending once both tickets are signed.
    const sums = (r.invs || []).map((no) => c.sums[no]).filter((v) => v && v.status !== 'Void');
    let iEnv = null;
    sums.forEach((v) => {
        const e = summaryEnvelope(v, c.inv);
        if (e && (!iEnv || num(e.RowID) > num(iEnv.RowID))) iEnv = e;
    });
    const fromSums = sums.map((v) => SUMMARY_STATUS[v.status]).filter(Boolean)
        .sort((a, b) => RANK.indexOf(a) - RANK.indexOf(b))[0] || '';
    // No summary over the row: the approval whose named period covers the
    // work date stands for it, and the row shows that invoice's number.
    if (!iEnv && !sums.length) {
        iEnv = periodEnvelope(String(r.d || '').slice(0, 10), c.inv);
        out.invoiceNo = iEnv ? invoicePeriod(iEnv).no : '';
    }
    out.invoice = envStatus(iEnv) || fromSums
        || (out.daily === STATUS.signed && out.weekly === STATUS.signed ? STATUS.pending : '');
    out.invoiceEnv = iEnv?.RowID || null;
    out.sigs.invoice = pick(iEnv);
    out.waiting.invoice = waitOn(iEnv);
    return out;
};

// ── Field Ticket: cached HeavyJob vs HeavyJob now ──

export const HJ_STATUS = {
    same: 'Unchanged',
    changed: 'Changed',
    removed: 'Removed from HeavyJob',
    added: 'Added in HeavyJob',
    fresh: 'New in HeavyJob',
    jdeOnly: 'JDE, no ticket',
};

/**
 * Stored rows (one snapshot per cell — currentRows) against the live cards
 * for the same dates. Each stored row gets hjNow / hjNowSt / hjNowOt / cardsNow
 * and a status; HeavyJob hours on a covered date that no stored row holds
 * come back as extra 'New in HeavyJob' rows. `ticketGap` flags a row whose
 * ticket printed different hours than HeavyJob held when it went out (the
 * reviewed time card at work, usually — not an edit). `tol`: the hours an
 * edit may move a row and still read Unchanged (a row added or zeroed out
 * always shows).
 */
export const compareHeavyJob = (stored, liveRows, liveCards = [], { tol = RECON_TOL } = {}) => {
    const byDate = {};
    (liveRows || []).forEach((r) => {
        const d = String(r.Work_Date || '').slice(0, 10);
        (byDate[d] = byDate[d] || []).push(r);
    });
    const live = {};
    Object.entries(byDate).forEach(([d, rows]) => {
        Object.values(hjRollup(rows, liveCards)).forEach((e) => { live[`${d}|${e.k}|${e.id}|${normCode(e.cc)}`] = { ...e, d }; });
    });
    const used = new Set();
    const out = (stored || []).map((r) => {
        const k = dayKey(r);
        const e = live[k];
        if (e) used.add(k);
        const nowSt = e ? r2(e.st) : 0;
        const nowOt = e ? r2(e.ot) : 0;
        const now = r2(nowSt + nowOt);
        const then = r2(r.hj);
        const status = Math.abs(now - then) <= RECON_TOL ? HJ_STATUS.same
            : then <= RECON_TOL ? HJ_STATUS.added
                : now <= RECON_TOL ? HJ_STATUS.removed
                    : Math.abs(now - then) <= tol ? HJ_STATUS.same
                        : HJ_STATUS.changed;
        return {
            ...r,
            // The SQL copy has no ST / OT split: the total stands, the split is blank.
            hjNow: now, hjNowSt: e?.unsplit ? null : nowSt, hjNowOt: e?.unsplit ? null : nowOt,
            cardsNow: e ? [...e.cards].join(',') : '',
            modNow: e?.mod || null,
            delta: r2(now - then),
            status,
            ticketGap: Math.abs(r2(r.ft) - then) > RECON_TOL,
        };
    });
    // HeavyJob time on a date the stored tickets cover that none of their
    // rows holds — somebody carded after the ticket went out.
    const covered = new Set((stored || []).map((r) => r.d));
    Object.entries(live).forEach(([k, e]) => {
        if (used.has(k) || !covered.has(e.d)) return;
        const labor = e.k === 'Labor';
        out.push({
            id: null, d: e.d, sh: '', k: e.k, cc: e.cc, ccd: e.ccd,
            emp: labor ? e.emp : '', name: labor ? e.name : '', unit: labor ? '' : e.unit, unitDesc: labor ? '' : e.unitDesc,
            title: labor ? e.title : '', cls: '', card: '',
            hjSt: 0, hjOt: 0, hj: 0, ft: 0,
            hjNow: r2(e.st + e.ot), hjNowSt: e.unsplit ? null : r2(e.st), hjNowOt: e.unsplit ? null : r2(e.ot),
            cardsNow: [...e.cards].join(','), modNow: e.mod || null,
            delta: r2(e.st + e.ot), status: HJ_STATUS.fresh, ticketGap: false,
        });
    });
    return out.sort(rowOrder);
};

// The JDE side a row carries (matchJde + the view's invoice hours / statuses).
const JDE_FIELDS = ['jdeSt', 'jdeOt', 'jde', 'jdePd', 'jdeKeys', 'jdeLines', 'jdePdLines', 'billedNow', 'invs', 'invHrs', 'invSt', 'invOt', 'jdeCc', 'jdeStatus', 'jdeDelta', 'st'];

/**
 * The Field Ticket tab's rows plus the JDE lines no ticket row claims
 * (matchJde's 'No field ticket' rows, `keyRows` without an id): a 'New in
 * HeavyJob' row for the same day, person / unit and cost code takes the JDE
 * side; the rest stand as 'JDE, no ticket' rows with the HeavyJob side blank.
 */
export const withJdeOnly = (rows, keyRows) => {
    const out = [...(rows || [])];
    const fresh = {};
    out.forEach((r, i) => { if (!r.id) fresh[dayKey(r)] = i; });
    (keyRows || []).filter((j) => !j.id).forEach((j) => {
        const i = fresh[dayKey(j)];
        if (i != null) {
            out[i] = { ...out[i], ...Object.fromEntries(JDE_FIELDS.filter((f) => f in j).map((f) => [f, j[f]])) };
            return;
        }
        out.push({ ...j, hjNow: null, hjNowSt: null, hjNowOt: null, delta: null, status: HJ_STATUS.jdeOnly, ticketGap: false });
    });
    return out.sort(rowOrder);
};

// ── Invoice: field ticket vs JDE cost history ──

export const JDE_STATUS = {
    match: 'Match',
    differs: 'JDE ≠ ticket',
    perDiem: 'Per diem differs',
    pending: 'Not in JDE yet',
    code: 'Cost code differs',
    orphan: 'No field ticket',
};

// The billing lines that carry ticket hours: labor straight time (51100) and
// overtime (51200), fleet equipment usage (52100), and rental usage lines
// (HeavyJob card hours on PO-<po>-<line> units, billingRentalUsage) — rental
// vouchers (52200) post with no hours.
const jdeKind = (t) => (t.ct === '51100' || t.ct === '51200' ? 'Labor' : t.ct === '52100' || isRentUsage(t) ? 'Equipment' : null);

// Form_Job_Cost_History_Key off the line (billingPricing keys its lines
// 'bl2_<jde key>#<n>'; the pre-key legacy hash has no JDE key to show).
export const jdeKeyOf = (t) => t.jk || (String(t.key || '').startsWith('bl2_') ? String(t.key).slice(4).replace(/#\d+$/, '') : '');

/**
 * Whether matchJde gives a billing line a place on the Labor & Equipment
 * grid: a per diem (51500) line with its person and date, or an ST / OT /
 * fleet (51100 / 51200 / 52100) line with hours, a date and its person or
 * unit. The Invoices view splits a summary's lines on it — these show on the
 * Labor & Equipment grid, the rest on the Third Party one.
 */
export const jdeClaims = (t) => {
    if (!t || !t.date) return false;
    if (t.ct === '51500') return Boolean(t.sid);
    const k = jdeKind(t);
    if (!k) return false;
    return Boolean(k === 'Labor' ? normId(t.sid) : normCode(t.unit)) && Boolean(num(t.hrs));
};

const billedOf = (lines) => {
    if (!lines.length) return '';
    const billed = lines.filter((t) => t.inv);
    if (billed.length === lines.length) return 'Billed';
    if (billed.length) return 'Partly billed';
    if (lines.every((t) => t.nb)) return 'Not billable';
    return 'Unbilled';
};

/**
 * The hours the billing summaries billed for a row's JDE lines: each line's
 * hours as its summary froze them (billedHrs, by cost-history key), else the
 * line's own — split the way JDE splits them, overtime off the 51200 lines
 * and straight time off the rest (51100, and 52100 fleet usage). All three
 * null while none of the lines is on a summary.
 */
export const invoiceSplit = (r, billedHrs = {}) => {
    const lines = (r.jdeLines || []).filter((t) => t.inv && jdeKind(t));
    if (!lines.length) return { invSt: null, invOt: null, invHrs: null };
    const hrs = (t) => num(billedHrs[t.key] ?? t.hrs);
    const ot = lines.filter((t) => t.ct === '51200').reduce((a, t) => a + hrs(t), 0);
    const st = lines.filter((t) => t.ct !== '51200').reduce((a, t) => a + hrs(t), 0);
    return { invSt: r2(st), invOt: r2(ot), invHrs: r2(st + ot) };
};
export const invoiceHours = (r, billedHrs = {}) => invoiceSplit(r, billedHrs).invHrs;

// A rental unit's rental PO off its HeavyJob code (PO-231557-1 -> 231557).
const poOf = (unit) => (/^PO-(\d+)-/i.exec(String(unit || '').trim()) || [])[1] || '';

/**
 * The invoice's side of every stored row: the JDE lines (billing txns) for
 * the same kind x person-or-unit x work date x cost code, their straight-time
 * and overtime hours, keys, and whether they are billed. A person-day whose
 * only JDE hours sit on a different cost code matches the one ticket row it
 * has that day, flagged 'Cost code differs'. JDE lines no ticket row claims
 * come back as 'No field ticket' rows. OT is payroll's call (over 40 in the
 * pay week), so the check compares total hours; the split is shown. `tol`:
 * the hours JDE may sit off the ticket and still Match (per diem, a count,
 * stays exact).
 */
export const matchJde = (stored, txns, { start, end, tol = RECON_TOL } = {}) => {
    const groups = {};
    // Per diem (51500) is per person-day, not per cost code: one line a day,
    // a reversal counts -1.
    const pdBy = {};
    // Equipment tiers off every equipment line the job has (not just this
    // week's): the fleet unit's JDE tier and the rate sheet row it bills as,
    // and a rental PO's tier.
    const tierBy = {};
    (txns || []).forEach((t) => {
        if (t.cls !== 'Equipment' || !t.tier) return;
        const u = normCode(t.unit);
        // JDE pads the PO (00231534); the HeavyJob rental code doesn't (PO-231534-1).
        const po = normId(t.po);
        [u, po ? `PO:${po}` : ''].filter(Boolean).forEach((k) => {
            if (!tierBy[k] || (!tierBy[k].ba && t.ba)) tierBy[k] = { tier: t.tier, ba: t.ba || '' };
        });
    });
    (txns || []).forEach((t) => {
        // jdeClaims is the one gate, so the Invoices view's split stays in
        // step with what this grid shows.
        if (!jdeClaims(t) || (start && t.date < start) || (end && t.date > end)) return;
        if (t.ct === '51500') {
            const pk = `${t.date}|${normId(t.sid)}`;
            const g = pdBy[pk] || (pdBy[pk] = { n: 0, lines: [] });
            g.n += num(t.cost) > 0 ? 1 : num(t.cost) < 0 ? -1 : 0;
            g.lines.push(t);
            return;
        }
        const k = jdeKind(t);
        const id = k === 'Labor' ? normId(t.sid) : normCode(t.unit);
        // The code JDE has the line on: a pending cost move (costMoves.js)
        // reads it on its new code everywhere else, but the ticket the
        // client signed carries the old one, as JDE still does.
        const cc = String(t.ccFrom || t.cc || '').trim();
        const gk = `${t.date}|${k}|${id}|${normCode(cc)}`;
        const g = groups[gk] || (groups[gk] = {
            gk, d: t.date, k, id, cc, ccd: t.ccDesc || '',
            who: t.who || '', sid: t.sid || '', unit: t.unit || '', eq: t.eq || '', cls: t.ba || '', title: t.jde || '',
            st: 0, ot: 0, lines: [],
        });
        if (t.ct === '51200') g.ot += num(t.hrs); else g.st += num(t.hrs);
        g.lines.push(t);
    });
    const take = (g) => ({
        jdeSt: r2(g.st), jdeOt: r2(g.ot), jde: r2(g.st + g.ot),
        jdeKeys: [...new Set(g.lines.map(jdeKeyOf).filter(Boolean))],
        jdeLines: g.lines,
        billedNow: billedOf(g.lines),
        invs: [...new Set(g.lines.map((t) => t.inv).filter(Boolean))],
    });
    const used = new Set();
    const out = (stored || []).map((r) => {
        const g = groups[dayKey(r)];
        // jdePd is re-derived below: a stored row carries its last Record's.
        if (!g) return { ...r, jdeSt: null, jdeOt: null, jde: null, jdePd: null, jdeKeys: [], jdeLines: [], jdePdLines: [], billedNow: '', invs: [], jdeCc: '' };
        used.add(g.gk);
        return { ...r, ...take(g), jdePd: null, jdePdLines: [], jdeCc: '' };
    });
    // Second pass: a person-day with exactly one unmatched ticket row takes
    // the JDE hours left over on another code that day.
    const leftover = Object.values(groups).filter((g) => !used.has(g.gk));
    leftover.forEach((g) => {
        const open = out.filter((r) => r.jde == null && r.d === g.d && r.k === g.k && idOf(r) === g.id);
        if (open.length !== 1) return;
        const i = out.indexOf(open[0]);
        used.add(g.gk);
        out[i] = { ...out[i], ...take(g), jdeCc: g.cc };
    });
    // Per diem onto one row of each person-day: the one HeavyJob put its
    // adder on, else the one with the most ticket hours.
    const pdRow = {};
    out.forEach((r, i) => {
        if (r.k !== 'Labor') return;
        const pk = `${r.d}|${idOf(r)}`;
        const cur = pdRow[pk];
        const better = cur == null || (num(r.hjPd) > 0 && !(num(out[cur].hjPd) > 0))
            || (!(num(out[cur].hjPd) > 0) && num(r.ft) > num(out[cur].ft));
        if (better) pdRow[pk] = i;
    });
    const usedPd = new Set();
    Object.entries(pdRow).forEach(([pk, i]) => {
        const g = pdBy[pk];
        const hasJde = out.some((r) => r.k === 'Labor' && `${r.d}|${idOf(r)}` === pk && r.jde != null);
        if (!g && !hasJde) return;
        usedPd.add(pk);
        out[i] = {
            ...out[i],
            jdePd: g ? g.n : 0,
            // The per diem lines themselves, for the cost history popup.
            jdePdLines: g ? g.lines : [],
            jdeKeys: [...new Set([...(out[i].jdeKeys || []), ...(g ? g.lines.map(jdeKeyOf).filter(Boolean) : [])])],
        };
    });
    const withStatus = out.map((r) => {
        const ft = r2(r.ft);
        const pdGap = r.jdePd != null && Math.abs(num(r.jdePd) - num(r.hjPd)) > RECON_TOL;
        const status = r.jde == null ? (ft > RECON_TOL ? JDE_STATUS.pending : pdGap ? JDE_STATUS.perDiem : JDE_STATUS.match)
            : r.jdeCc ? JDE_STATUS.code
                : Math.abs(r2(r.jde) - ft) > tol ? JDE_STATUS.differs
                    : pdGap ? JDE_STATUS.perDiem : JDE_STATUS.match;
        // Equipment: the tier the matched JDE lines carry, else the unit's /
        // rental PO's anywhere on the job, else what the last Record kept.
        let eqTier = r.eqTier || '';
        let eqBill = r.eqBill || '';
        if (r.k === 'Equipment') {
            const line = (r.jdeLines || []).find((t) => t.tier);
            const m = line ? { tier: line.tier, ba: line.ba || '' } : tierBy[normCode(r.unit)] || tierBy[`PO:${normId(poOf(r.unit))}`] || null;
            if (m) { eqTier = m.tier || eqTier; eqBill = m.ba || eqBill; }
        }
        return { ...r, eqTier, eqBill, jdeStatus: status, jdeDelta: r.jde == null ? null : r2(r.jde - ft) };
    });
    Object.values(groups).filter((g) => !used.has(g.gk)).forEach((g) => {
        const labor = g.k === 'Labor';
        withStatus.push({
            id: null, d: g.d, sh: '', k: g.k, cc: g.cc, ccd: g.ccd,
            emp: labor ? g.sid : '', name: labor ? g.who : '', unit: labor ? '' : g.unit, unitDesc: labor ? '' : g.eq,
            title: g.title, cls: g.cls, card: '', hj: 0, hjSt: 0, hjOt: 0, hjPd: 0, ft: 0,
            eqTier: labor ? '' : (tierBy[normCode(g.unit)]?.tier || ''), eqBill: labor ? '' : (tierBy[normCode(g.unit)]?.ba || ''),
            ...take(g), jdePdLines: [], jdeCc: '', jdeStatus: JDE_STATUS.orphan, jdeDelta: r2(g.st + g.ot),
        });
    });
    // JDE per diem for a person-day no row holds.
    Object.entries(pdBy).forEach(([pk, g]) => {
        if (usedPd.has(pk) || !g.n) return;
        const [d] = pk.split('|');
        const t = g.lines[0];
        withStatus.push({
            id: null, d, sh: '', k: 'Labor', cc: String(t.cc || '').trim(), ccd: t.ccDesc || '',
            emp: t.sid || '', name: t.who || '', unit: '', unitDesc: '', title: t.jde || '', cls: t.ba || '', card: '',
            hj: 0, hjSt: 0, hjOt: 0, hjPd: 0, ft: 0, eqTier: '', eqBill: '',
            jdeSt: null, jdeOt: null, jde: null, jdePd: g.n, jdeKeys: [...new Set(g.lines.map(jdeKeyOf).filter(Boolean))],
            jdeLines: g.lines, jdePdLines: [], billedNow: billedOf(g.lines), invs: [...new Set(g.lines.map((x) => x.inv).filter(Boolean))],
            jdeCc: '', jdeStatus: JDE_STATUS.orphan, jdeDelta: null,
        });
    });
    return withStatus.sort(rowOrder);
};

/**
 * What Record writes back per stored row: the JDE match and the three
 * statuses with their signers (rows carry statusesOf's output as `st`).
 */
export const recordPayload = (rows) => (rows || [])
    .filter((r) => r.id)
    .map((r) => ({
        id: r.id,
        jdeKey: (r.jdeKeys || []).join(',') || null,
        jdeSt: r.jde == null ? null : r.jdeSt,
        jdeOt: r.jde == null ? null : r.jdeOt,
        jde: r.jde,
        jdePd: r.jdePd == null ? null : r.jdePd,
        invHrs: r.invHrs == null ? null : r.invHrs,
        invSt: r.invHrs == null ? null : r.invSt ?? null,
        invOt: r.invHrs == null ? null : r.invOt ?? null,
        eqTier: r.k === 'Equipment' ? r.eqTier || null : null,
        eqBill: r.k === 'Equipment' ? r.eqBill || null : null,
        inv: (r.invs || []).join(',') || null,
        daily: r.st?.daily || null,
        weekly: r.st?.weekly || null,
        invoice: r.st?.invoice || null,
        dailyEnv: r.st?.dailyEnv || null,
        weeklyEnv: r.st?.weeklyEnv || null,
        invoiceEnv: r.st?.invoiceEnv || null,
        sigs: r.st?.sigs || { daily: [], weekly: [], invoice: [] },
    }));

// Date, then day crew before night, labor before equipment, then name/unit
// and cost code — the order the tickets print in.
const SH_RANK = { Day: 0, Night: 1, '': 2 };
const KIND_RANK = { Labor: 0, Equipment: 1 };
export const rowOrder = (a, b) => String(a.d).localeCompare(String(b.d))
    || (SH_RANK[a.sh] ?? 2) - (SH_RANK[b.sh] ?? 2)
    || (KIND_RANK[a.k] ?? 2) - (KIND_RANK[b.k] ?? 2)
    || String(a.name || a.unit || '').localeCompare(String(b.name || b.unit || ''))
    || String(a.cc).localeCompare(String(b.cc));

/**
 * The grid's rows as a tree: week (Monday–Sunday) > work date > either the
 * person or unit (`by` 'who') or the set of JDE lines the row matched (`by`
 * 'jde'). Weeks and dates run oldest first, or newest first with
 * `newestFirst`; a day's groups keep the order their first rows print in
 * (rowOrder); the rows with no JDE line yet come last as one group.
 *
 * `byWeek` drops the date level: week > person (or JDE key), so a person's
 * week sums in one row (the Weekly summary view — Seth 11/4: "show people by
 * week so it's easier to see overtime hours per week is correct"). People
 * run labor first, then by name; a person's rows run by date.
 */
export const groupRows = (rows, by, weekEnding, { newestFirst = false, byWeek = false } = {}) => {
    const dir = newestFirst ? -1 : 1;
    const weeks = new Map();
    const at = (map, k, make) => {
        if (!map.has(k)) map.set(k, make());
        return map.get(k);
    };
    const idOf = (r) => (by === 'jde' ? `j|${[...(r.jdeKeys || [])].sort().join(',')}`
        : r.k === 'Equipment' ? `e|${normCode(r.unit)}` : `p|${normId(r.emp) || normCode(r.name)}`);
    (rows || []).forEach((r) => {
        const we = weekEnding(r.d) || '';
        const w = at(weeks, we, () => ({ key: `w|${we}`, level: 0, we, rows: [], days: new Map(), groups: new Map() }));
        const id = idOf(r);
        w.rows.push(r);
        if (byWeek) {
            at(w.groups, id, () => ({ key: `${w.key}|${id}`, level: 2, none: id === 'j|', rows: [] })).rows.push(r);
            return;
        }
        const day = at(w.days, r.d, () => ({ key: `d|${r.d}`, level: 1, d: r.d, rows: [], groups: new Map() }));
        const g = at(day.groups, id, () => ({ key: `${day.key}|${id}`, level: 2, none: id === 'j|', rows: [] }));
        day.rows.push(r);
        g.rows.push(r);
    });
    const sortedWeeks = [...weeks.values()].sort((a, b) => dir * a.we.localeCompare(b.we));
    if (byWeek) {
        const label = (g) => String(g.rows[0].name || g.rows[0].unit || '');
        return sortedWeeks.map((w) => ({
            key: w.key, level: 0, we: w.we, rows: w.rows,
            children: [...w.groups.values()]
                .sort((a, b) => Number(a.none) - Number(b.none)
                    || (by === 'jde' ? 0 : (KIND_RANK[a.rows[0].k] ?? 2) - (KIND_RANK[b.rows[0].k] ?? 2) || label(a).localeCompare(label(b))))
                .map((g) => ({ ...g, rows: g.rows.slice().sort((a, b) => dir * String(a.d).localeCompare(String(b.d)) || rowOrder(a, b)) })),
        }));
    }
    return sortedWeeks.map((w) => ({
        key: w.key, level: 0, we: w.we, rows: w.rows,
        children: [...w.days.values()].sort((a, b) => dir * String(a.d).localeCompare(String(b.d))).map((d) => ({
            key: d.key, level: 1, d: d.d, rows: d.rows,
            children: [...d.groups.values()].sort((a, b) => Number(a.none) - Number(b.none)),
        })),
    }));
};

/**
 * Expected overtime on each ticketed labor row, the way the invoice bills it
 * (Seth 11/4: a column on the Weekly summary to check each person's OT for
 * the week): the first `after` hours of the Monday–Sunday week are straight
 * time across every job the person worked, walked in date order (the lower
 * job number first within a day, a day's rows in ticket order); the rest is
 * overtime. This job's hours are the field ticket's; `otherHours` is
 * { [we]: { [id]: { [job]: { [date]: hrs } } } } off JDE payroll
 * (billingInvoiceExceptions.otherHoursFromPay).
 *
 * Returns { byId: Map(row id → { ot, key }), weeks: Map(key → { we, id,
 * here, other: { [job]: hrs }, total, ot, otHere }) }, key `${we}|${id}`.
 * JDE payroll splits a week's OT across jobs in proportion instead, so a
 * person who also worked another job can read differently from JDE OT here.
 */
export const expectedOt = (rows, otherHours, weekEnding, { job = '', after = 40 } = {}) => {
    const people = new Map();
    (rows || []).forEach((r) => {
        if (r.k !== 'Labor' || !r.id || !normId(r.emp) || !r.d) return;
        const we = weekEnding(r.d) || '';
        const id = normId(r.emp);
        const key = `${we}|${id}`;
        if (!people.has(key)) people.set(key, { we, id, rows: [] });
        people.get(key).rows.push(r);
    });
    const byId = new Map();
    const weeks = new Map();
    people.forEach(({ we, id, rows: mine }, key) => {
        const other = (((otherHours || {})[we] || {})[id]) || {};
        const entries = [
            ...mine.map((r) => ({ d: r.d, job: String(job), r, h: num(r.ft) })),
            ...Object.entries(other).flatMap(([j, days]) => Object.entries(days || {}).map(([d, h]) => ({ d, job: String(j), h: num(h) }))),
        ].sort((a, b) => a.d.localeCompare(b.d) || a.job.localeCompare(b.job) || (a.r && b.r ? rowOrder(a.r, b.r) : 0));
        let run = 0;
        let otHere = 0;
        entries.forEach((e) => {
            const st = Math.max(0, Math.min(e.h, after - run));
            run += e.h;
            if (e.r) {
                byId.set(e.r.id, { ot: r2(e.h - st), key });
                otHere += e.h - st;
            }
        });
        const otherTotals = {};
        Object.entries(other).forEach(([j, days]) => {
            const h = r2(Object.values(days || {}).reduce((a, v) => a + num(v), 0));
            if (h > 0) otherTotals[j] = h;
        });
        const here = r2(mine.reduce((a, r) => a + num(r.ft), 0));
        const total = r2(run);
        weeks.set(key, { we, id, here, other: otherTotals, total, ot: r2(Math.max(0, total - after)), otHere: r2(otHere) });
    });
    return { byId, weeks };
};

/** The job's snapshots grouped into Monday–Sunday weeks, newest first. */
export const snapshotWeeks = (snapshots, weekEnding) => {
    const weeks = {};
    (snapshots || []).forEach((s) => {
        const first = String(s.First_Date || '').slice(0, 10);
        const last = String(s.Last_Date || first).slice(0, 10);
        if (!first) return;
        // A document can straddle two weeks; it counts in both.
        [weekEnding(first), weekEnding(last)].filter(Boolean).forEach((we) => {
            const w = weeks[we] || (weeks[we] = { we, snaps: [] });
            if (!w.snaps.includes(s)) w.snaps.push(s);
        });
    });
    return Object.values(weeks).sort((a, b) => b.we.localeCompare(a.we));
};
